import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  FaRegFileAlt,
  FaRegEdit,
  FaChartBar,
  FaGlobe,
  FaRegClone,
  FaRulerHorizontal,
  FaAlignLeft,
  FaQuoteRight,
  FaUpload,
  FaUserCheck,
  FaRegCommentDots,
  FaCheckDouble,
  FaShieldAlt,
  FaIdBadge,
  FaUserEdit,
  FaHandshake,
  FaRegCopyright,
  FaBookOpen,
  FaTrophy,
  FaChalkboardTeacher,
  FaPaperPlane,
  FaDownload,
} from 'react-icons/fa';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import {
  Container,
  Section,
  Reveal,
  Kicker,
  ButtonPrimary,
  ButtonGhost,
  HudArrowButton,
  DotLattice,
  CopperGlow,
  Orbital3D,
} from '../components/ui';

/*
 * The submission pipeline — five interactive stages rendered as a HUD
 * instrument panel: cut-corner tech frames, hex icon badges, lit trace.
 */

const STAGES = [
  {
    key: 'prepare',
    label: 'Prepare',
    title: 'Choose your category & format',
    intro: 'We welcome submissions in the following categories:',
    points: [
      { icon: <FaRegFileAlt />, term: 'Full Papers', detail: 'Original research contributions (6 pages maximum)' },
      { icon: <FaRegEdit />, term: 'Short Papers', detail: 'Work-in-progress or preliminary results (4 pages maximum)' },
      { icon: <FaChartBar />, term: 'Review Papers', detail: 'Comprehensive surveys of current research (6 pages maximum)' },
      { icon: <FaGlobe />, term: 'Language', detail: 'Papers must be written in English and represent original, unpublished work' },
    ],
  },
  {
    key: 'submit',
    label: 'Submit',
    title: 'Format to IEEE & upload via CMT',
    intro: 'Original unpublished papers are invited as per the following guideline:',
    points: [
      { icon: <FaRegClone />, term: 'Template', detail: 'Use the IEEE conference paper format template' },
      { icon: <FaRulerHorizontal />, term: 'Length', detail: 'Full paper not exceeding 6 pages, including figures and tables' },
      { icon: <FaAlignLeft />, term: 'Abstract', detail: 'Include abstract (150–250 words) and 4–6 keywords' },
      { icon: <FaQuoteRight />, term: 'Figures & References', detail: 'All figures, tables and equations clearly labeled; IEEE citation style' },
      { icon: <FaUpload />, term: 'Portal', detail: 'Submit as PDF through the CMT submission system (opening soon)' },
    ],
  },
  {
    key: 'review',
    label: 'Review',
    title: 'Double-blind peer review',
    intro: 'All submissions undergo a rigorous double-blind peer review process:',
    points: [
      { icon: <FaUserCheck />, term: 'Experts', detail: 'Each paper is reviewed by at least two independent experts in the field' },
      { icon: <FaRegCommentDots />, term: 'Feedback', detail: 'Authors receive detailed feedback within 4–6 weeks of submission' },
      { icon: <FaCheckDouble />, term: 'Criteria', detail: 'Papers evaluated on novelty, technical quality, clarity, and relevance' },
      { icon: <FaShieldAlt />, term: 'Integrity', detail: 'Plagiarism detection software is used to ensure originality' },
    ],
  },
  {
    key: 'finalize',
    label: 'Camera-Ready',
    title: 'Finalize your accepted paper',
    intro: 'Once accepted, complete these author requirements:',
    points: [
      { icon: <FaIdBadge />, term: 'Registration', detail: 'At least one author must register and attend the conference' },
      { icon: <FaUserEdit />, term: 'Author Info', detail: 'Complete author information, affiliations, and a 50–100 word biography' },
      { icon: <FaHandshake />, term: 'Disclosure', detail: 'Declare any conflicts of interest or funding sources; co-authors must approve' },
      { icon: <FaRegCopyright />, term: 'Copyright', detail: 'Copyright transfer form must be submitted for accepted papers' },
    ],
  },
  {
    key: 'present',
    label: 'Present',
    title: 'Present in Guwahati & get indexed',
    intro: 'Accepted and presented papers will be published in the proceedings and submitted for inclusion in:',
    points: [
      { icon: <FaBookOpen />, term: 'IEEE Xplore', detail: "IEEE Xplore Digital Library (subject to IEEE Xplore's scope and quality requirements)" },
      { icon: <FaTrophy />, term: 'Awards', detail: 'Best paper awards will be presented during the conference' },
      { icon: <FaChalkboardTeacher />, term: 'Presentation', detail: 'Accepted papers must be presented at the conference by at least one author' },
    ],
  },
];

/* ============================ HERO ============================ */

const HeroWrap = styled.header`
  padding: clamp(8rem, 14vw, 11rem) 0 clamp(3rem, 6vw, 5rem);
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid var(--line);
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.6rem, 6.5vw, 4.75rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  max-width: 16ch;

  em {
    font-style: normal;
    background: linear-gradient(115deg, var(--copper-bright) 10%, var(--copper) 55%, var(--copper-deep) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const HeroLead = styled.p`
  color: var(--ink-dim);
  font-size: clamp(1rem, 1.5vw, 1.2rem);
  max-width: 48ch;
  margin-top: 1.25rem;
  line-height: 1.7;
`;

/* sparse HUD details — brackets and signal dots */
const sparkle = keyframes`
  0%, 100% { opacity: 0.25; }
  50% { opacity: 0.9; }
`;

const HudLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  .bracket {
    position: absolute;
    border: 1px solid rgba(224, 141, 60, 0.35);
  }

  .bracket.tl {
    top: 96px;
    left: clamp(0.6rem, 2vw, 1.6rem);
    width: 14px;
    height: 110px;
    border-right: none;
  }

  .bracket.tr {
    top: 120px;
    right: clamp(0.6rem, 2vw, 1.6rem);
    width: 14px;
    height: 200px;
    border-left: none;
    border-color: rgba(34, 31, 26, 0.18);
  }

  .dot {
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--copper-bright);
    box-shadow: 0 0 8px rgba(242, 176, 94, 0.8);
    animation: ${sparkle} 3.4s ease-in-out infinite;
  }

  @media (max-width: 760px) {
    display: none;
  }
`;

/* ===================== TECH FRAME PRIMITIVES =================== */

const TechPanel = styled.div`
  --cut: 30px;
  position: relative;
  isolation: isolate;
  background: linear-gradient(
    110deg,
    rgba(126, 132, 138, 0.55) 0%,
    rgba(44, 49, 56, 0.95) 16%,
    rgba(224, 141, 60, 0.9) 48%,
    rgba(42, 47, 54, 0.96) 80%,
    rgba(126, 132, 138, 0.55) 100%
  );
  clip-path: polygon(
    var(--cut) 0,
    calc(100% - var(--cut)) 0,
    100% var(--cut),
    100% calc(100% - var(--cut)),
    calc(100% - var(--cut)) 100%,
    var(--cut) 100%,
    0 calc(100% - var(--cut)),
    0 var(--cut)
  );
  filter: drop-shadow(0 22px 36px rgba(0, 0, 0, 0.45));
  padding: clamp(1.9rem, 4vw, 3rem);
  min-height: 380px;

  &::before {
    content: '';
    position: absolute;
    inset: 2px;
    z-index: -1;
    background: radial-gradient(120% 150% at 85% -20%, rgba(224, 141, 60, 0.1), #ffffff 50%, #ffffff 100%);
    clip-path: inherit;
  }
`;

const PanelHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.4rem;

  h2 {
    font-size: clamp(1.4rem, 2.6vw, 1.9rem);
    font-weight: 700;
    margin-bottom: 0.8rem;
  }

  .intro {
    color: var(--ink-dim);
    line-height: 1.7;
  }
`;

/* hexagonal icon badge */
const HexBadge = styled.span`
  --hex-size: 46px;
  position: relative;
  isolation: isolate;
  width: var(--hex-size);
  height: var(--hex-size);
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--copper-bright);
  font-size: 1rem;
  background: linear-gradient(140deg, rgba(242, 176, 94, 0.85), rgba(176, 94, 30, 0.6));
  clip-path: polygon(50% 0, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%);

  &::before {
    content: '';
    position: absolute;
    inset: 1.5px;
    z-index: -1;
    background: linear-gradient(180deg, #f1ede4, #ffffff);
    clip-path: inherit;
  }
`;

const SpecRows = styled.dl`
  display: grid;
  gap: 0.7rem;
  margin-top: 1.6rem;
`;

const SpecRow = styled.div`
  display: grid;
  grid-template-columns: auto 175px 1fr;
  align-items: center;
  gap: 1.2rem;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.55);
  padding: 0.85rem 1.2rem;
  transition: border-color 0.25s var(--ease-out), background 0.25s var(--ease-out),
    transform 0.25s var(--ease-out);

  &:hover {
    border-color: rgba(224, 141, 60, 0.4);
    background: rgba(224, 141, 60, 0.05);
    transform: translateX(4px);
  }

  dt {
    font-family: var(--font-mono);
    font-size: 0.74rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--copper-bright);
  }

  dd {
    color: var(--ink-dim);
    font-size: 0.94rem;
    line-height: 1.6;
    border-left: 1px solid var(--line);
    padding-left: 1.2rem;
  }

  @media (max-width: 680px) {
    grid-template-columns: auto 1fr;

    dt {
      align-self: center;
    }

    dd {
      grid-column: 1 / -1;
      border-left: none;
      padding-left: 0;
      border-top: 1px dashed var(--line);
      padding-top: 0.6rem;
    }
  }
`;

/* ========================= PIPELINE BAR ======================== */

const PipelineBar = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  margin: 0 0 2.4rem;

  &::before {
    content: '';
    position: absolute;
    top: 28px;
    left: 10%;
    right: 10%;
    height: 1px;
    background: var(--line-strong);
  }

  &::after {
    content: '';
    position: absolute;
    top: 27px;
    left: 10%;
    width: calc(80% * ${(p) => Math.max(p.$progress, 0.11)});
    height: 3px;
    border-radius: 3px;
    background: linear-gradient(90deg, var(--copper-deep), var(--copper-bright));
    box-shadow: 0 0 14px rgba(224, 141, 60, 0.65);
    transition: width 0.4s var(--ease-out);
  }

  @media (max-width: 700px) {
    grid-template-columns: repeat(5, minmax(86px, 1fr));
    overflow-x: auto;
    padding-bottom: 0.5rem;
  }
`;

const StageBtn = styled.button`
  position: relative;
  z-index: 1;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
  padding: 0;

  .node {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--font-mono);
    font-size: 0.74rem;
    font-weight: 600;
    background: ${(p) =>
      p.$active
        ? 'radial-gradient(circle at 32% 28%, var(--copper-bright), var(--copper-deep))'
        : 'var(--bg-elev)'};
    color: ${(p) => (p.$active ? '#16100a' : p.$done ? 'var(--copper-bright)' : 'var(--ink-faint)')};
    border: 1px solid ${(p) => (p.$done || p.$active ? 'var(--copper)' : 'var(--line-strong)')};
    box-shadow: ${(p) =>
      p.$active
        ? '0 0 0 5px rgba(236, 233, 225, 1), 0 0 0 6px var(--copper), 0 0 26px rgba(224, 141, 60, 0.55)'
        : '0 0 0 5px rgba(236, 233, 225, 1), 0 0 0 6px var(--line)'};
    transition: all 0.3s var(--ease-out);
  }

  .label {
    font-family: var(--font-mono);
    font-size: 0.68rem;
    font-weight: ${(p) => (p.$active ? 600 : 400)};
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${(p) => (p.$active ? 'var(--copper-bright)' : 'var(--ink-faint)')};
    transition: color 0.2s;
  }

  &:hover .node {
    border-color: var(--copper);
    transform: scale(1.07);
  }

  &:hover .label {
    color: var(--ink);
  }
`;

/* hex-cut arrow buttons */
const ArrowNav = styled.div`
  display: flex;
  gap: 0.7rem;
  flex: none;
`;

/* ======================== FOOTER DRESSING ====================== */

const GridFloor = styled.div`
  position: absolute;
  left: -12%;
  right: -12%;
  bottom: -50px;
  height: 260px;
  background-image: linear-gradient(rgba(224, 141, 60, 0.13) 1px, transparent 1px),
    linear-gradient(90deg, rgba(224, 141, 60, 0.13) 1px, transparent 1px);
  background-size: 58px 58px;
  transform: perspective(440px) rotateX(58deg);
  transform-origin: bottom;
  mask-image: linear-gradient(180deg, transparent 0%, black 70%);
  -webkit-mask-image: linear-gradient(180deg, transparent 0%, black 70%);
  pointer-events: none;
  opacity: 0.55;
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.1rem;
  justify-content: center;
  margin-top: clamp(2.6rem, 5vw, 3.8rem);
  position: relative;
`;

const FormatNote = styled.p`
  text-align: center;
  color: var(--ink-faint);
  font-size: 0.9rem;
  margin-top: 1.6rem;
  position: relative;

  a {
    color: var(--copper-bright);
    border-bottom: 1px solid rgba(242, 176, 94, 0.35);

    &:hover {
      color: var(--copper);
      border-color: var(--copper);
    }
  }
`;

const Acknowledgement = styled.div`
  max-width: 760px;
  margin: 2.5rem auto 0;
  padding: 1.5rem 1.75rem;
  border: 1px solid var(--line);
  color: var(--ink-dim);
  line-height: 1.7;

  h2 {
    color: var(--ink);
    font-size: 1.1rem;
    margin-bottom: 0.7rem;
  }
`;

export default function Submissions() {
  const [stage, setStage] = useState(0);
  const current = STAGES[stage];

  return (
    <>
      <HeroWrap>
        <DotLattice />
        <CopperGlow style={{ top: '-280px', right: '-180px' }} />
        <Orbital3D
          size="min(560px, 52vw)"
          opacity={0.55}
          style={{ top: '4%', right: '-6%' }}
        />
        <HudLayer aria-hidden="true">
          <span className="bracket tl" />
          <span className="bracket tr" />
          <span className="dot" style={{ top: '30%', right: '8%' }} />
          <span className="dot" style={{ top: '52%', right: '22%', animationDelay: '1.1s' }} />
          <span className="dot" style={{ top: '64%', right: '30%', animationDelay: '2s' }} />
        </HudLayer>
        <Container style={{ position: 'relative' }}>
          <Reveal>
            <Kicker index="04">Paper Submissions</Kicker>
            <HeroTitle>
              Write it. Submit it. <em>Present it.</em>
            </HeroTitle>
            <HeroLead>
              Five stages from manuscript to IEEE Xplore. Walk the pipeline — every requirement, in
              order.
            </HeroLead>
          </Reveal>
        </Container>
      </HeroWrap>

      <Section style={{ overflow: 'hidden' }}>
        <GridFloor aria-hidden="true" />
        <Container style={{ position: 'relative' }}>
          <Reveal>
            <Kicker index={`${stage + 1} of ${STAGES.length}`}>The Submission Pipeline</Kicker>

            <PipelineBar $progress={stage / (STAGES.length - 1)}>
              {STAGES.map((s, i) => (
                <StageBtn
                  key={s.key}
                  $active={stage === i}
                  $done={stage > i}
                  onClick={() => setStage(i)}
                  aria-label={`Stage ${i + 1}: ${s.label}`}
                  aria-current={stage === i ? 'step' : undefined}
                >
                  <span className="node">{String(i + 1).padStart(2, '0')}</span>
                  <span className="label">{s.label}</span>
                </StageBtn>
              ))}
            </PipelineBar>

            <TechPanel aria-live="polite">
              <PanelHead>
                <div>
                  <h2>{current.title}</h2>
                  <p className="intro">{current.intro}</p>
                </div>
                <ArrowNav>
                  <HudArrowButton
                    onClick={() => setStage((s) => Math.max(0, s - 1))}
                    disabled={stage === 0}
                    aria-label="Previous stage"
                  >
                    <FiArrowLeft />
                  </HudArrowButton>
                  <HudArrowButton
                    $accent
                    onClick={() => setStage((s) => Math.min(STAGES.length - 1, s + 1))}
                    disabled={stage === STAGES.length - 1}
                    aria-label="Next stage"
                  >
                    <FiArrowRight />
                  </HudArrowButton>
                </ArrowNav>
              </PanelHead>

              <SpecRows>
                {current.points.map((point) => (
                  <SpecRow key={point.term}>
                    <HexBadge aria-hidden="true">{point.icon}</HexBadge>
                    <dt>{point.term}</dt>
                    <dd>{point.detail}</dd>
                  </SpecRow>
                ))}
              </SpecRows>
            </TechPanel>
          </Reveal>

          <Reveal>
            <CtaRow>
              <ButtonPrimary as="span" $disabled aria-disabled="true">
                <FaPaperPlane aria-hidden="true" /> Submit Your Paper — Soon
              </ButtonPrimary>
              <ButtonGhost
                href="https://www.ieee.org/conferences/publishing/templates"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaRegFileAlt aria-hidden="true" /> IEEE Guidelines
              </ButtonGhost>
              <ButtonGhost as="span" $disabled aria-disabled="true">
                <FaDownload aria-hidden="true" /> Download Brochure — Soon
              </ButtonGhost>
            </CtaRow>
            <FormatNote>
              Full paper, not exceeding 6 pages (including figures and tables), in{' '}
              <a
                href="https://www.ieee.org/conferences/publishing/templates"
                target="_blank"
                rel="noopener noreferrer"
              >
                IEEE format
              </a>{' '}
              through CMT (opening soon), on or before the deadline.
            </FormatNote>
            <Acknowledgement>
              <h2>CMT Acknowledgement</h2>
              <p>
                The Microsoft CMT service was used for managing the peer-reviewing process for this
                conference. This service was provided for free by Microsoft and they bore all expenses,
                including costs for Azure cloud services as well as for software development and support.
              </p>
            </Acknowledgement>
          </Reveal>
        </Container>
      </Section>
    </>
  );
}
